import { useEffect, useCallback, useState, useRef } from 'react';
import { useApp } from '../context/AppContext';

export default function PhotoModal() {
  const { photos, selectedPhotoId, setSelectedPhotoId } = useApp();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const currentIndex = photos.findIndex(p => p.id === selectedPhotoId);
  const currentPhoto = photos[currentIndex];

  const goToNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      setIsLoaded(false);
      setSelectedPhotoId(photos[currentIndex + 1].id);
    }
  }, [currentIndex, photos, setSelectedPhotoId]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setIsLoaded(false);
      setSelectedPhotoId(photos[currentIndex - 1].id);
    }
  }, [currentIndex, photos, setSelectedPhotoId]);

  const handleClose = useCallback(() => {
    setSelectedPhotoId(null);
  }, [setSelectedPhotoId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, goToNext, goToPrevious]);

  // Touch handling for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
    
    setTouchStart(null);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!currentPhoto) {
    return null;
  }

  const isVideo = currentPhoto.fileType === 'video' || currentPhoto.mimeType?.startsWith('video/');
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < photos.length - 1;

  return (
    <div 
      className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center"
      onClick={handleClose}
      ref={modalRef}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 z-20 text-white/80 hover:text-white transition-colors p-2"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Navigation arrows */}
      {hasPrevious && (
        <button
          onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white/60 hover:text-white transition-colors p-2 hidden md:block"
        >
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goToNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white/60 hover:text-white transition-colors p-2 hidden md:block"
        >
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Photo counter */}
      <div className="absolute top-4 left-4 z-20 text-white/60 font-body text-sm">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Main content */}
      <div
        className="relative max-w-4xl w-full mx-4 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Polaroid frame */}
        <div className="bg-white p-3 md:p-4 shadow-2xl">
          {/* Media container */}
          <div className="relative bg-autumn-cream/50 aspect-[4/3] md:aspect-auto md:max-h-[60vh] overflow-hidden">
            {isVideo ? (
              <video
                key={currentPhoto.id}
                src={currentPhoto.fileUrl}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                onLoadedData={() => setIsLoaded(true)}
              />
            ) : (
              <>
                <img
                  key={currentPhoto.id}
                  src={currentPhoto.mediumUrl || currentPhoto.fileUrl}
                  alt={`Photo by ${currentPhoto.uploaderName}`}
                  className={`w-full h-full object-contain transition-opacity duration-300 ${
                    isLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setIsLoaded(true)}
                />
                {!isLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 border-4 border-autumn-gold/20 border-t-autumn-gold rounded-full animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Bottom section - Polaroid style */}
          <div className="pt-4 pb-2 md:pt-6 md:pb-4 text-center">
            <p className="font-display text-lg md:text-xl text-autumn-brown">
              {currentPhoto.uploaderName}
            </p>
          </div>
        </div>

        {/* Mobile swipe hint */}
        <p className="text-center text-white/40 text-sm font-body mt-4 md:hidden">
          Swipe to navigate
        </p>
      </div>
    </div>
  );
}

